import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HandHelping, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  findDelegation,
  helperLandingPath,
  storeDelegation,
} from '../components/competition/round-delegation/storage';
import { errorCodeOf } from '../services/api-error';
import { roundDelegationsService, type RoundDelegationInvitation } from '../services/round-delegations';

/** As the server allows (RoundDelegation::NAME_MAX). */
const NAME_MAX = 60;

/**
 * Where a scanned QR code or a shared link lands: "Mario R. asks you to help
 * record the results of this round". Somebody with no account says who they
 * are and accepts; the phone keeps the link's token and the secret it is given
 * (storage.ts) and goes to the page it will score from.
 *
 * **One phone per link.** Once taken, the link opens nothing on another phone —
 * but on this one it simply opens the page again, because the phone still holds
 * the secret that goes with it.
 */
export default function RoundHelpInvitation() {
  const { slug, roundId, token } = useParams<{ slug: string; roundId: string; token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [invitation, setInvitation] = useState<RoundDelegationInvitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const taken = findDelegation(token);

  useEffect(() => {
    if (!token || taken) return;
    let cancelled = false;
    roundDelegationsService
      .invitation(token)
      .then((data) => {
        if (!cancelled) setInvitation(data);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, taken]);

  if (!slug || !roundId || !token) return <Navigate to="/not-found" replace />;

  // This phone took the link already: straight back to work.
  if (taken) return <Navigate to={helperLandingPath(taken)} replace />;

  const accept = async (event: FormEvent) => {
    event.preventDefault();
    if (!invitation || !name.trim()) return;

    setSaving(true);
    setError(null);
    try {
      const accepted = await roundDelegationsService.accept(token, name.trim());
      const entry = {
        token,
        secret: accepted.device_secret,
        competitionSlug: accepted.competition.slug,
        competitionId: accepted.competition.id,
        roundId: accepted.round.id,
        roundName: accepted.round.name,
        kind: accepted.kind,
        grantorName: accepted.grantor_name,
      };
      storeDelegation(entry);
      navigate(helperLandingPath(entry), { replace: true });
    } catch (failure) {
      const code = errorCodeOf(failure);
      if (code === 'delegation_already_accepted') {
        setInvitation({ ...invitation, status: 'accepted' });
      } else if (code === 'delegation_revoked') {
        setInvitation({ ...invitation, status: 'revoked' });
      } else if (code === 'round_not_delegable') {
        setInvitation({ ...invitation, status: 'stopped' });
      } else {
        setError(failure instanceof Error ? failure.message : t('errors.unexpectedError'));
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="my-8 flex items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" aria-label={t('common.loading')} />
      </div>
    );
  }

  if (notFound || !invitation) {
    return (
      <div className="my-8 flex justify-center px-4">
        <Card className="w-full max-w-md" data-testid="help-invitation-missing">
          <CardHeader>
            <CardTitle>{t('roundHelp.invitation.missingTitle')}</CardTitle>
            <CardDescription>{t('roundHelp.invitation.missing')}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button variant="outline" className="w-full" asChild>
              <Link to={`/competitions/${slug}`}>{t('roundHelp.toCompetition')}</Link>
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  const closedReason =
    invitation.status === 'accepted' || invitation.status === 'revoked' || invitation.status === 'stopped'
      ? invitation.status
      : null;

  return (
    <div className="my-8 flex justify-center px-4">
      <Card className="w-full max-w-md" data-testid="help-invitation" data-status={invitation.status}>
        <CardHeader>
          <HandHelping className="mb-2 size-8 text-primary" aria-hidden />
          <CardTitle>{t('roundHelp.invitation.title', { name: invitation.grantor_name })}</CardTitle>
          <CardDescription>
            {t(
              invitation.kind === 'team'
                ? 'roundHelp.invitation.descriptionTeam'
                : 'roundHelp.invitation.descriptionAthlete',
              {
                name: invitation.grantor_name,
                round: invitation.round.name,
                competition: invitation.competition.name,
              }
            )}
          </CardDescription>
        </CardHeader>

        {closedReason ? (
          <>
            <CardContent>
              <p className="text-sm" data-testid="help-invitation-closed">
                {t(`roundHelp.invitation.${closedReason}`, { name: invitation.grantor_name })}
              </p>
            </CardContent>
            <CardFooter>
              <Button variant="outline" className="w-full" asChild>
                <Link to={`/competitions/${invitation.competition.slug}`}>{t('roundHelp.toCompetition')}</Link>
              </Button>
            </CardFooter>
          </>
        ) : (
          <form onSubmit={accept}>
            <CardContent className="space-y-2">
              <Label htmlFor="helper-name">{t('roundHelp.invitation.nameLabel')}</Label>
              <Input
                id="helper-name"
                value={name}
                maxLength={NAME_MAX}
                autoComplete="name"
                onChange={(event) => setName(event.target.value)}
                placeholder={t('roundHelp.invitation.namePlaceholder')}
                data-testid="helper-name"
              />
              <p className="text-xs text-muted-foreground">
                {t('roundHelp.invitation.nameHint', { name: invitation.grantor_name })}
              </p>
              {error && (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              )}
            </CardContent>
            <CardFooter>
              <Button type="submit" className="w-full" disabled={saving || !name.trim()} data-testid="help-accept">
                {saving && <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />}
                {t('roundHelp.invitation.accept')}
              </Button>
            </CardFooter>
          </form>
        )}
      </Card>
    </div>
  );
}
